import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { products, projects, newsArticles, categories } from '../data/sbbData';
import { Search, ChevronRight, FileText, Layers, Briefcase, BookOpen } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function SearchPage() {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const query = searchParams.get('q') || '';

  // Search algorithm matching products, categories, projects, and articles
  const matchedProducts = products.filter((p) => 
    p.name.toLowerCase().includes(query.toLowerCase()) || 
    p.summary.toLowerCase().includes(query.toLowerCase())
  );

  const matchedCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.description.toLowerCase().includes(query.toLowerCase())
  );

  const matchedProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.summary.toLowerCase().includes(query.toLowerCase()) ||
    p.client.toLowerCase().includes(query.toLowerCase())
  );

  const matchedArticles = newsArticles.filter((a) =>
    a.title.toLowerCase().includes(query.toLowerCase()) ||
    a.excerpt.toLowerCase().includes(query.toLowerCase())
  );

  const totalResults = matchedProducts.length + matchedCategories.length + matchedProjects.length + matchedArticles.length;

  return (
    <div className="search-page">
      {/* Page Hero */}
      <PageHero eyebrow="Pencarian Global" title={t('search_results')} subtitle={<>Menampilkan hasil pencarian untuk: <strong>"{query}"</strong></>} image="/reference-images/hero-slider.webp" />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('search_results')}</span></li>
          </ul>
        </div>
      </div>

      {/* Results Content */}
      <section className="section">
        <div className="container">
          <p className="results-count-text">
            Ditemukan <strong>{totalResults}</strong> hasil pencarian untuk "{query}".
          </p>

          {totalResults > 0 ? (
            <div className="search-results-list">
              {/* Products */}
              {matchedProducts.map((p) => (
                <div key={p.slug} className="search-result-item">
                  <div className="result-badge product">
                    <Layers size={14} /> Produk
                  </div>
                  <h3 className="result-item-title">
                    <Link to={`/produk-layanan/detail/${p.slug}`}>{p.name}</Link>
                  </h3>
                  <p className="result-item-desc">{p.summary}</p>
                </div>
              ))}

              {/* Categories */}
              {matchedCategories.map((c) => (
                <div key={c.slug} className="search-result-item">
                  <div className="result-badge category">
                    <Layers size={14} /> Kategori Solusi
                  </div>
                  <h3 className="result-item-title">
                    <Link to={`/produk-layanan/${c.slug}`}>{c.name}</Link>
                  </h3>
                  <p className="result-item-desc">{c.description}</p>
                </div>
              ))}

              {/* Projects */}
              {matchedProjects.map((p) => (
                <div key={p.slug} className="search-result-item">
                  <div className="result-badge project">
                    <Briefcase size={14} /> Proyek
                  </div>
                  <h3 className="result-item-title">
                    <Link to={`/proyek/${p.slug}`}>{p.name}</Link>
                  </h3>
                  <p className="result-item-desc">{p.summary}</p>
                </div>
              ))}

              {/* Articles */}
              {matchedArticles.map((a) => (
                <div key={a.slug} className="search-result-item">
                  <div className="result-badge article">
                    <BookOpen size={14} /> Artikel / Siaran Pers
                  </div>
                  <h3 className="result-item-title">
                    <Link to={`/blog/${a.slug}`}>{a.title}</Link>
                  </h3>
                  <p className="result-item-desc">{a.excerpt}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state-box text-center">
              <Search size={48} color="var(--color-text-muted)" style={{ margin: '0 auto 16px auto' }} />
              <h3>{t('no_results')}</h3>
              <p style={{ color: 'var(--color-text-muted)', marginTop: '8px', marginBottom: '24px' }}>
                Coba gunakan kata kunci yang lebih umum seperti "PakCrete", "SpeedCrete", "Tol", atau "ISO".
              </p>
              <Link to="/produk-layanan" className="btn btn-primary">
                {t('nav_all_products')}
              </Link>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .results-count-text {
          font-size: 16px;
          color: var(--color-ink);
          margin-bottom: 28px;
        }

        .search-results-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .search-result-item {
          background-color: #ffffff;
          border: 1px solid var(--color-border);
          border-radius: var(--border-radius-md);
          padding: 24px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .search-result-item:hover {
          border-color: var(--color-brand-primary);
          box-shadow: var(--shadow-subtle);
        }

        .result-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 4px;
          margin-bottom: 10px;
          text-transform: uppercase;
        }

        .result-badge.product { background-color: #eef4ff; color: #155eef; }
        .result-badge.category { background-color: #f8f0fe; color: #9b2c2c; }
        .result-badge.project { background-color: #f0fdf4; color: #166534; }
        .result-badge.article { background-color: #fff7ed; color: #c2410c; }

        .result-item-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--color-ink);
          margin-bottom: 8px;
        }

        .result-item-title a:hover {
          color: var(--color-brand-primary);
        }

        .result-item-desc {
          font-size: 14px;
          color: var(--color-text-muted);
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
}
